import { createFileRoute, Link } from "@tanstack/react-router";
import { BackToDashboard } from "@/components/BackToDashboard";
import { useEffect, useState } from "react";
import { claimPublicHandle } from "@/lib/venue-groups.functions";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/public-profile")({
  head: () => ({ meta: [{ title: "Public profile — The Hospitality Reserve" }] }),
  component: PublicProfilePage,
});

function PublicProfilePage() {
  const [handle, setHandle] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [desired, setDesired] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      const { data } = await supabase.from("profiles")
        .select("public_handle, role, full_name").eq("id", u.user.id).maybeSingle();
      if (data) {
        setHandle((data.public_handle as string | null) ?? null);
        setRole((data.role as string | null) ?? null);
        if (!data.public_handle && data.full_name) setDesired(String(data.full_name));
      }
    })();
  }, []);

  async function save() {
    setSaving(true);
    const r = await claimPublicHandle({ data: { desired } });
    setSaving(false);
    if ("error" in r) { toast.error(r.error); return; }
    setHandle(r.handle);
    toast.success(`Public profile live at /${role === "venue" ? "v" : "b"}/${r.handle}`);
  }

  const prefix = role === "venue" ? "v" : "b";
  const liveUrl = handle ? `/${prefix}/${handle}` : null;

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <BackToDashboard />
      <header>
        <h1 className="font-display text-2xl">Public profile</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Claim your shareable URL. {role === "venue"
            ? "Your venue gets a public micro-site with the 'Staffed by The Hospitality Reserve' badge and a roster preview."
            : "Show off reviews and certifications on a SEO-friendly profile page."}
        </p>
      </header>

      <section className="border border-border rounded p-4 space-y-3">
        <label className="text-sm">Desired handle</label>
        <div className="flex gap-2 items-center">
          <span className="text-muted-foreground text-sm">thehospitalityreserve.com/{prefix}/</span>
          <input value={desired} onChange={(e) => setDesired(e.target.value)}
            className="border border-border rounded px-3 py-2 flex-1 bg-background"
            placeholder="your-handle" />
        </div>
        <button onClick={save} disabled={saving || desired.trim().length < 3}
          className="bg-accent text-accent-foreground px-4 py-2 rounded disabled:opacity-50">
          {handle ? "Update handle" : "Claim handle"}
        </button>
        {liveUrl && (
          <p className="text-sm">Live at <Link to={liveUrl} className="underline text-accent">{liveUrl}</Link></p>
        )}
      </section>
    </div>
  );
}
